import React, { useContext, useMemo, useState } from 'react';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { FiVideo } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { CollabDataContext } from 'contexts/collabDataContext';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import HelpSection from '../components/HelpSection';
import ViewGuidelinesSheet from '../components/creative-brief/ViewGuidelinesSheet';

import { DeliverableLink, ContentSubmissionUnit, ContentRequirement, SocialChannel, isVoided } from '@foodfluence/shared';
import { getGroupedContent, getGroupStatus } from '../../../utils/groupedContentUtils';
import {
  getUnitRelatedLinks,
  getEffectiveContentStatus,
  getUnitRelatedLinkRefs,
  getOutstandingPostChannels,
} from '../../../utils/contentSubmissionUtils';
import { getCollabContentRequirements, getContentTypeDisplayName, getContentTypeCount, hasNewContent } from '../../../utils/contentUtils';
import { getAcceptedFileTypes } from '../../../utils/contentUploadUtils';
import { uploadAndSubmitUgcContent } from '../../../utils/ugcSubmissionUtils';
import { deliverableApi } from '../../../services/api/deliverable';
import SubmissionPhaseIndicator from '../components/SubmissionPhaseIndicator';
import { getUGCCollabPhase } from 'utils/influencerCollabPhase';
import { getChannelDisplayName } from '../../../utils/channelUtils';
import ContentUnitCard from '../components/ContentUnitCard';
import {
  EditsBanner,
  ShopifyCodeBox,
  SubmissionEmpty,
  SubmissionGroups,
  SubmissionHeader,
  SUBMISSION_MAX_W,
} from '../components/SubmissionPageParts';
import { contentCardStatus } from '../utils/submissionCards';
import { ff } from '../../../theme';
import DraftUploadModal from '../components/DraftUploadModal';
import MultiContentUploadModal from '../components/MultiContentUploadModal';
import InputLinkUgcModal from '../components/InputLinkUgcModal';
import CompletedModal, { CompletedPostLink } from '../components/CompletedContentModal';
import PendingReviewModal from '../components/ContentUnderReviewModal';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase';

interface MergedContentItem {
  requirementId: string;
  contentKey: string;
  unit: ContentSubmissionUnit;
  requirement?: ContentRequirement;
  links: DeliverableLink[];
}

const UgcSubmissionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const collabId = queryParams.get('id');

  const collabData = useContext(CollabDataContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const collab = collabData.find(c => c.id === collabId);
  const campaign = campaignData[collabId!];
  const clients = useContext(ClientDataContext);
  const { influencerData } = useContext(InfluencerDataContext);
  const client = clients[campaign?.clientID];

  const deliverableLinksRecord = useMemo(() => (collab?.deliverableLinks || {}) as Record<string, DeliverableLink[]>, [collab]);
  const [guidelinesRequirement, setGuidelinesRequirement] = useState<ContentRequirement | null>(null);

  const contentMap = useMemo(() => (collab?.content || {}) as Record<string, ContentSubmissionUnit>, [collab]);
  const hasContent = Object.keys(contentMap).length > 0;

  const contentRequirementsMap = useMemo(
    () => getCollabContentRequirements(collab, campaign),
    [collab, campaign]
  );

  const mergedContent = useMemo(() => {
    // Use resolved content requirements order and sort by requirement ID
    if (contentRequirementsMap) {
      return Object.entries(contentRequirementsMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([requirementId, requirement]) => {
          // Get all content units for this requirement
          const contentUnits = getGroupedContent(contentMap, requirementId);
          const groupStatus = getGroupStatus(contentUnits);

          if (contentUnits.length === 0) {
            // `getGroupedContent` drops voided units, so an empty group can mean the brand withdrew
            // this requirement. Keep showing it, dimmed, rather than letting it vanish silently.
            const voidedUnit = Object.entries(contentMap)
              .find(([_, unit]) => unit.requirementId === requirementId && isVoided(unit));
            if (voidedUnit) {
              return {
                requirementId,
                contentKey: voidedUnit[0],
                unit: voidedUnit[1],
                requirement,
                links: [],
                voided: true,
              };
            }
            return null;
          }

          // Represent the whole group by whichever unit needs the influencer's
          // attention (rejected, else unsubmitted), falling back to the first
          // unit only when the group is fully approved or all pending review.
          // The merged unit's status always reflects the group aggregate, not
          // just whichever unit happens to sort first — a group isn't done
          // until every unit in it is.
          const { key: firstKey, unit: firstUnit } = groupStatus.representative ?? contentUnits[0];
          return {
            requirementId,
            contentKey: firstKey,
            unit: {
              ...firstUnit,
              status: groupStatus.status,
              contentRejected: groupStatus.needsResubmission,
              feedback: groupStatus.feedback ?? firstUnit.feedback
            },
            requirement,
            links: getUnitRelatedLinks(requirementId, deliverableLinksRecord),
            voided: false,
          };
        }).filter(Boolean);
    }

    // Fallback to contentMap order if no campaign requirements
    return Object.entries(contentMap)
      .sort(([_, a], [__, b]) => a.requirementId.localeCompare(b.requirementId))
      .map(([contentKey, unit]) => ({
        requirementId: unit.requirementId,
        contentKey,
        unit,
        requirement: undefined,
        links: getUnitRelatedLinks(unit.requirementId, deliverableLinksRecord),
        voided: isVoided(unit),
      }));
  }, [contentMap, deliverableLinksRecord, contentRequirementsMap]);

  const handleViewGuidelines = (requirement: ContentRequirement) => {
    setGuidelinesRequirement(requirement);
  };

  const handleBack = () => {
    // location.key is 'default' only for the first entry in the history stack
    // (deep link from SMS/push, or a refresh), where navigate(-1) would leave
    // the app. Everywhere else, go back to wherever the influencer came from.
    if (location.key !== 'default') {
      navigate(-1);
      return;
    }
    navigate(collabId ? `/collabs/collab-details/${collabId}` : '/collabs');
  };

  // --- Submission modals -----------------------------------------------------
  const [activeItem, setActiveItem] = useState<MergedContentItem | null>(null);
  const [isSingleUploadOpen, setIsSingleUploadOpen] = useState(false);
  const [isMultiUploadOpen, setIsMultiUploadOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isCompletedModalOpen, setIsCompletedModalOpen] = useState(false);
  const [completedContentUrls, setCompletedContentUrls] = useState<string[]>([]);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewContentUrls, setReviewContentUrls] = useState<string[]>([]);
  const [reviewContentType, setReviewContentType] = useState<'link' | 'file'>('file');
  const [reviewStage, setReviewStage] = useState<'brand' | 'foodfluence'>('brand');
  const [reviewDeliverableKey, setReviewDeliverableKey] = useState('');

  const closeModals = () => {
    setIsSingleUploadOpen(false);
    setIsMultiUploadOpen(false);
    setIsLinkModalOpen(false);
    setActiveItem(null);
  };

  const closeReviewModal = () => {
    setIsReviewModalOpen(false);
    setReviewContentUrls([]);
  };

  const closeCompletedModal = () => {
    setIsCompletedModalOpen(false);
    setCompletedContentUrls([]);
    setActiveItem(null);
  };

  const getCompletedContentUrl = (item: MergedContentItem): string => {
    const approvedLink = item.links.find(l => l.status === 'approved');
    return approvedLink?.verifiedUGCLink || item.unit.fileUrl || '';
  };

  const getCompletedPostLinks = (item: MergedContentItem): CompletedPostLink[] =>
    getUnitRelatedLinkRefs(item.requirementId, deliverableLinksRecord)
      .filter(({ link }) => link.status === 'approved' && link.userLink)
      .map(({ deliverableKey, link }) => ({
        label: getChannelDisplayName(deliverableKey as SocialChannel),
        url: link.userLink!,
      }));

  const itemDisplayName = (requirement?: ContentRequirement): string => {
    if (!requirement) return 'Content';
    const base = getContentTypeDisplayName(requirement.duration, requirement.type);
    const count = getContentTypeCount(requirement);
    return count > 1 ? `${count}x ${base}` : base;
  };

  const toast = useToast();

  const resolveContentUrl = async (fileUrl: string): Promise<string | null> => {
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    try {
      return await getDownloadURL(ref(storage, fileUrl));
    } catch (error) {
      console.error('Error resolving submitted content URL:', error);
      toast({
        title: 'Unable to load content',
        description: 'There was a problem loading your submitted content.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return null;
    }
  };

  const openContentReview = async (item: MergedContentItem) => {
    const fileUrls = getGroupedContent(contentMap, item.requirementId)
      .map(({ unit }) => unit.fileUrl)
      .filter((url): url is string => Boolean(url));
    if (fileUrls.length === 0) return;

    const displayUrls = (
      await Promise.all(fileUrls.map(resolveContentUrl))
    ).filter((url): url is string => Boolean(url));
    if (displayUrls.length === 0) return;

    setReviewContentType('file');
    setReviewStage('brand');
    setReviewDeliverableKey('');
    setReviewContentUrls(displayUrls);
    setIsReviewModalOpen(true);
  };

  const openContentCompleted = async (item: MergedContentItem) => {
    const fileUrls = getGroupedContent(contentMap, item.requirementId)
      .map(({ unit }) => unit.fileUrl)
      .filter((url): url is string => Boolean(url));

    const urlsToResolve = fileUrls.length > 0
      ? fileUrls
      : [getCompletedContentUrl(item)].filter((url): url is string => Boolean(url));

    if (urlsToResolve.length === 0) return;

    const displayUrls = (
      await Promise.all(urlsToResolve.map(resolveContentUrl))
    ).filter((url): url is string => Boolean(url));
    if (displayUrls.length === 0) return;

    setActiveItem(item);
    setCompletedContentUrls(displayUrls);
    setIsCompletedModalOpen(true);
  };

  const openLinkReview = (item: MergedContentItem) => {
    const pendingRef = getUnitRelatedLinkRefs(item.requirementId, deliverableLinksRecord)
      .find(({ link }) => link.status === 'pendingVerification');
    const postUrl = pendingRef?.link.userLink;
    if (!postUrl) return;

    setReviewContentType('link');
    setReviewStage('foodfluence');
    setReviewDeliverableKey(pendingRef.deliverableKey);
    setReviewContentUrls([postUrl]);
    setIsReviewModalOpen(true);
  };

  const handleCardClick = (item: MergedContentItem) => {
    const status = getEffectiveContentStatus(item.unit, item.links);
    const count = item.requirement ? getContentTypeCount(item.requirement) : 1;

    // Content upload phase — single piece reuses DraftUploadModal, 2+ uses the
    // multi-content sheet.
    if (status === 'pendingSubmission' || status === 'contentRejected') {
      setActiveItem(item);
      if (count >= 2) setIsMultiUploadOpen(true);
      else setIsSingleUploadOpen(true);
      return;
    }

    if (status === 'pendingVerification') {
      void openContentReview(item);
      return;
    }
    if (status === 'linkPendingVerification') {
      openLinkReview(item);
      return;
    }

    // Live-link phase — submit links for every platform of this piece at once.
    if (status === 'linkPendingSubmission' || status === 'linkNeedsEdit') {
      const hasActionableLinks = getUnitRelatedLinkRefs(item.requirementId, deliverableLinksRecord)
        .some(({ link }) => link.rejectionReason || link.status === 'pendingSubmission');
      if (hasActionableLinks) {
        setActiveItem(item);
        setIsLinkModalOpen(true);
        return;
      }
    }

    if (status === 'approved') {
      void openContentCompleted(item);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };


  const ugcPhase = collab ? getUGCCollabPhase(collab) : 'content';
  const ugcHasChannels = Object.values(contentMap).some(u => u.channels && u.channels.length > 0)
    || Object.values(contentRequirementsMap || {}).some(r => r.channels && r.channels.length > 0);

  if (!collab || !campaign) {
    return <Box>Loading...</Box>;
  }

  type CardItem = NonNullable<(typeof mergedContent)[number]>;
  const cardStatusOf = (item: CardItem): string =>
    item.voided ? 'voided' : getEffectiveContentStatus(item.unit, item.links);
  const cards = (mergedContent as Array<CardItem | null>).filter((item): item is CardItem => Boolean(item));
  const groupOf = (item: CardItem) => contentCardStatus(cardStatusOf(item)).group;
  const todoCount = cards.filter(c => groupOf(c) === 'todo').length;
  const reviewCount = cards.filter(c => groupOf(c) === 'review').length;
  const editCount = cards.filter(c => ['contentRejected', 'linkNeedsEdit'].includes(cardStatusOf(c))).length;
  const brandName = client?.accountName || 'The brand';

  return (
    <>
    <ViewGuidelinesSheet
      isOpen={guidelinesRequirement !== null}
      onClose={() => setGuidelinesRequirement(null)}
      requirement={guidelinesRequirement}
      clientId={campaign?.clientID}
      clientName={client?.accountName}
    />
    <Box minH="100vh" bg="white" fontFamily="display" color={ff.ink}>
      <Box maxW={SUBMISSION_MAX_W} mx="auto" pb="32px">
        <SubmissionHeader
          eyebrow={[client?.accountName, campaign?.name].filter(Boolean).join(' · ') || 'Collab'}
          title="Submit content"
          onBack={handleBack}
          client={client}
        >
          {hasContent && (
            <Text fontSize="14px" color={ff.body} lineHeight="normal">
              {todoCount} to do · {reviewCount} in review
            </Text>
          )}
        </SubmissionHeader>

        <Flex px="20px" pt="18px" direction="column" gap="18px">
          {/* Phase indicator */}
          <SubmissionPhaseIndicator
            ugcPhase={ugcPhase}
            ugcHasChannels={ugcHasChannels}
          />

          {editCount > 0 && (
            <EditsBanner
              title={`${editCount} ${editCount === 1 ? 'revision' : 'revisions'} requested`}
              body="Tap the red cards below to see the feedback, then re-upload."
            />
          )}

          {/* Help Section */}
          <HelpSection
            collab={collab}
            campaign={campaign}
            client={client}
          />

          <ShopifyCodeBox collab={collab} campaign={campaign} client={client} onCopy={handleCopy} />

          {/* Content cards — one per requirement group; withdrawn requirements stay listed (dimmed) */}
          {hasContent && (
            <SubmissionGroups
              items={cards}
              groupOf={groupOf}
              render={(item) => {
                const { requirementId, contentKey, unit, requirement, links, voided } = item;
                const status = cardStatusOf(item);
                const linkReason = links.find(l => l.rejectionReason)?.rejectionReason;
                return (
                  <Flex key={requirementId} direction="column" gap="2px">
                    <ContentUnitCard
                      index={cards.indexOf(item)}
                      unit={unit}
                      isVoided={voided}
                      contentType={requirement?.type as 'video' | 'photo' | undefined}
                      // A withdrawn requirement's count is already decremented to 0, and
                      // getContentTypeCount falls back to 1 — so naming it by count would read
                      // "1x Video" for something nobody owes.
                      name={requirement && !voided ? `${getContentTypeCount(requirement)}x ${getContentTypeDisplayName(requirement.duration, requirement.type)}` : undefined}
                      statusOverride={getEffectiveContentStatus(unit, links)}
                      outstandingChannels={getOutstandingPostChannels(requirementId, deliverableLinksRecord)}
                      contentCount={requirement ? getContentTypeCount(requirement) : undefined}
                      feedback={status === 'contentRejected' ? unit.feedback : linkReason}
                      feedbackFrom={status === 'contentRejected' ? brandName : 'FoodFluence'}
                      onClick={() => handleCardClick({ requirementId, contentKey, unit, requirement, links })}
                    />
                    {!voided && requirement && (
                      <Box
                        as="button"
                        type="button"
                        alignSelf="flex-start"
                        minH="44px"
                        px="4px"
                        fontSize="14px"
                        fontWeight={600}
                        color={ff.ink}
                        textDecoration="underline"
                        onClick={() => handleViewGuidelines(requirement)}
                      >
                        View requirements →
                      </Box>
                    )}
                  </Flex>
                );
              }}
            />
          )}

          {/* No Content Message */}
          {!hasContent && (
            <SubmissionEmpty icon={FiVideo} title="Nothing to upload yet" body="No content requirements found for this collab." />
          )}
        </Flex>
      </Box>
    </Box>

    {/* Single-piece UGC content — reuses the draft upload bottom sheet */}
    {activeItem && (
      <DraftUploadModal
        isOpen={isSingleUploadOpen}
        onClose={closeModals}
        deliverableKey=""
        linkIndex={0}
        showFeedback={getEffectiveContentStatus(activeItem.unit, activeItem.links) === 'contentRejected'}
        rejectionReason={activeItem.unit.feedback || null}
        feedbackPdfUrl={activeItem.unit.editRequestPdf || null}
        modalHeader={itemDisplayName(activeItem.requirement)}
        clientName={client?.accountName || ''}
        campaign={campaign}
        accept={activeItem.requirement ? getAcceptedFileTypes(activeItem.requirement) : undefined}
        submitLabel={
          getEffectiveContentStatus(activeItem.unit, activeItem.links) === 'contentRejected'
            ? 'Submit revision'
            : 'Submit content'
        }
        uploadLabel={`Upload ${activeItem.requirement?.type === 'video' ? 'video' : 'photo'}`}
        existingContentUrl={
          getEffectiveContentStatus(activeItem.unit, activeItem.links) === 'contentRejected'
            ? activeItem.unit.fileUrl
            : undefined
        }
        onSubmitFile={async (file, onProgress) => {
          await uploadAndSubmitUgcContent(
            { collabId: collab.id, clientId: client?.id || '', contentKey: activeItem.requirementId, file, influencerId: influencerData?.id },
            onProgress,
          );
          if (hasNewContent(contentMap, [activeItem.requirementId])) {
            deliverableApi.notifyContentReview({ collabId: collab.id }, influencerData?.id);
          }
        }}
      />
    )}

    {/* Multi-piece UGC content (2+) — dedicated multi-slot bottom sheet */}
    {activeItem && activeItem.requirement && (
      <MultiContentUploadModal
        isOpen={isMultiUploadOpen}
        onClose={closeModals}
        requirement={activeItem.requirement}
        requirementId={activeItem.requirementId}
        contentMap={contentMap}
        collabId={collab.id}
        clientId={client?.id || ''}
        modalHeader={itemDisplayName(activeItem.requirement)}
      />
    )}

    {/* Live post links — one row per platform for this content piece */}
    {activeItem && (
      <InputLinkUgcModal
        isOpen={isLinkModalOpen}
        onClose={closeModals}
        requirementId={activeItem.requirementId}
        modalHeader={itemDisplayName(activeItem.requirement)}
        clientName={client?.accountName || ''}
        clientId={client?.id || ''}
        approvedContentUrl={activeItem.unit.status === 'approved' ? activeItem.unit.fileUrl : undefined}
      />
    )}

    <CompletedModal
      isOpen={isCompletedModalOpen}
      onClose={closeCompletedModal}
      title="Content completed!"
      subtitle={activeItem ? itemDisplayName(activeItem.requirement) : ''}
      body="Your content has been approved."
      contentUrls={completedContentUrls}
      postLinks={activeItem ? getCompletedPostLinks(activeItem) : undefined}
    />

    <PendingReviewModal
      isOpen={isReviewModalOpen}
      onClose={closeReviewModal}
      contentUrls={reviewContentUrls}
      contentType={reviewContentType}
      reviewStage={reviewStage}
      deliverableKey={reviewDeliverableKey}
    />
    </>
  );
};

export default UgcSubmissionPage;